import GetAppIcon from "@mui/icons-material/GetApp";
import { Box, Button, Container, IconButton, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import { Theme, useTheme } from "@mui/material/styles";
import CertificateRegenerationDialog from "components/CertificateRegenerationDialog";
import CourseTitleDateFilter from "components/CourseTitleDateFilter";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { isValid } from "date-fns";
import { Guid } from "guid-typescript";
import { makeStyles } from "helpers/StyleHelper";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
  CourseTitleModel,
  CourseTitlesRequest,
  IUserCertificateModel,
  ListRequestOfBaseFilter,
  ListRequestOfUserCertificatesFilter,
  ListResponseOfUserCertificateModel,
  OrganizationUserDetails,
  UserCertificateDownloadRequest,
  UserCertificatesFilter,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import { convertToUTC } from "utils/dateUtils";

const useStyles = makeStyles((theme: Theme) => ({
  regenerateCertificates: {
    margin: "30px 0",
  },
  confirmationMessage: {
    margin: "15px 0",
  },
}));

const UserCertificatesPage = () => {
  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();
  const userSkuSpecified = !!userSkuId && Guid.isGuid(userSkuId);
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState(
    "userDeliverablesCompletion.completionDate",
  );
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [
    userCertificateDownloadRequest,
    setUserCertificateToDownload,
  ] = useState<UserCertificateDownloadRequest | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [
    selectedCourseTitle,
    setSelectedCourseTitle,
  ] = useState<CourseTitleModel | null>(null);
  const [issueDateFrom, setIssueDateFrom] = useState<Date | undefined>();
  const [issueDateTo, setIssueDateTo] = useState<Date | undefined>();
  const {
    organizationUserService,
    userCertificateService,
    userDetailsUrl,
    userCourseUrl,
  } = useGlobalContext();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    },
  );

  const certificateDownloadUrlQuery = useQuery<string>(
    ["userCertificateDownload", userCertificateDownloadRequest],
    () => userCertificateService.getDownloadUrl(userCertificateDownloadRequest),
    {
      enabled: !!userCertificateDownloadRequest,
    },
  );

  useEffect(() => {
    if (
      certificateDownloadUrlQuery.data &&
      !certificateDownloadUrlQuery.isLoading
    ) {
      window.open(certificateDownloadUrlQuery.data, "_blank");
    }
  }, [certificateDownloadUrlQuery.data, certificateDownloadUrlQuery.isLoading]);

  const user = userResult.data;
  const title = t(
    { id: "userCertificatesLabel" },
    {
      name: getUserDisplayName(user, t({ id: "user" })),
    },
  );

  useTitle(title);

  const {
    isError,
    data,
    error,
    isLoading: certificatesLoading,
  } = useQuery<ListResponseOfUserCertificateModel>(
    [
      "userCertificates",
      userId,
      page,
      rowsPerPage,
      orderBy,
      order,
      selectedCourseTitle,
      issueDateFrom,
      issueDateTo,
    ],
    () =>
      userCertificateService.getAllUserCertificates(
        new ListRequestOfUserCertificatesFilter({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: new UserCertificatesFilter({
            userId,
            userSkuId: userSkuSpecified ? userSkuId : undefined,
            searchText: selectedCourseTitle?.name || undefined,
            issueDateFrom: isValid(issueDateFrom)
              ? convertToUTC(issueDateFrom)
              : undefined,
            issueDateTo: isValid(issueDateTo)
              ? convertToUTC(issueDateTo)
              : undefined,
          }),
        }),
      ),
    {
      enabled: !!userId,
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    setPage(0);
  }, [selectedCourseTitle, issueDateFrom, issueDateTo]);

  if (userResult.isLoading || certificatesLoading) return <Loader />;

  const userCertificates =
    data?.data?.map((x) => {
      return { ...x, id: x.userCertificateId! };
    }) ?? [];

  const cells: Array<TableCellType> = [
    {
      accessor: "courseTitle",
      customSortAccessor: "userDeliverablesCompletionDetail.courseTitle",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "titleLabel" }),
    },
    {
      accessor: "professionName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "professionLabel" }),
    },
    {
      accessor: "qualificationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "accreditationBodyLabel" }),
    },
    {
      accessor: "completionDate",
      customSortAccessor: "userDeliverablesCompletion.completionDate",
      align: "left",
      width: 0,
      type: "date",
      label: t({ id: "issueDateLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: (row: { value: IUserCertificateModel }) => (
        <IconButton
          disabled={certificateDownloadUrlQuery.isLoading}
          onClick={() => {
            if (
              row.value.userCertificateId ===
              userCertificateDownloadRequest?.userCertificateId
            ) {
              window.open(certificateDownloadUrlQuery.data, "_blank");
            } else {
              setUserCertificateToDownload(
                UserCertificateDownloadRequest.fromJS({
                  userCertificateId: row.value.userCertificateId!,
                  organizationId: row.value.organizationId,
                  userId: userId!,
                }),
              );
            }
          }}
        >
          <GetAppIcon />
        </IconButton>
      ),
      label: t({ id: "certificate" }),
    },
  ];

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const fetchCourseTitles = (request: ListRequestOfBaseFilter) => {
    const listRequest = new CourseTitlesRequest({
      userId: userId,
      organizationId: user?.organizationId,
      ...request,
    });
    return userCertificateService.getUserCertificatesCourseTitles(listRequest);
  };

  return (
    <Container>
      {userSkuSpecified ? (
        <RedirectBackButton
          redirectRoute={userCourseUrl(userId, userSkuId)}
          labelKey="backToUserCourse"
        />
      ) : (
        <RedirectBackButton
          redirectRoute={userDetailsUrl(userId)}
          labelKey="backToUser"
        />
      )}

      <h1>{title}</h1>

      <CourseTitleDateFilter
        cacheKey="certificates"
        fetchCourseTitles={fetchCourseTitles}
        selectedCourseTitle={selectedCourseTitle}
        onCourseTitleChange={setSelectedCourseTitle}
        dateFrom={issueDateFrom}
        dateTo={issueDateTo}
        onDateFromChange={setIssueDateFrom}
        onDateToChange={setIssueDateTo}
        dateRangeLabel={t({ id: "issueDateLabel" })}
      />

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: userCertificates ?? [],
          loading: certificatesLoading,
          dense: true,
          title: t({ id: "certificates" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />

      <CertificateRegenerationDialog
        open={regenerateDialogOpen}
        onClose={() => setRegenerateDialogOpen(false)}
        userId={userId!}
      />

      <Box sx={styles.regenerateCertificates}>
        <Typography variant="h5">
          <T id="regenerateCertificatesLabel" />
        </Typography>
        <Typography sx={styles.confirmationMessage}>
          <T id="regenerateCertificatesWarning" />
        </Typography>
        <Typography sx={styles.confirmationMessage}>
          <T id="regenerateCertificatesConfirmationMessage" />
        </Typography>
        <Button
          variant="outlined"
          disabled={regenerateDialogOpen}
          onClick={() => setRegenerateDialogOpen(true)}
        >
          <T id="regenerateCertificatesLabel" />
        </Button>
      </Box>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default UserCertificatesPage;
